import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  STORE_USER_FETCH_BY_TOKEN,
  STORE_USER,
  STORE_STATUS_INITIALIZED,
} from 'constants/store'
import { Company, ErrorStore, FeatureFlagResponse, User } from '@types'
import {
  createGenericSlice,
  GenericState,
  createReducers,
  ExtraReducers,
} from 'stores/generics'
import { Payload } from 'stores'

export type UserState = User & {
  currentCompany: Company
  features: FeatureFlagResponse
  userValidatedByOwner: boolean
}

type State = GenericState<UserState>

export const initialState: State = {
  data: {
    companies: [],
    display_name: '',
    email: '',
    imageUrl: '',
    id: '',
    is_admin: false,
    document_number: '',
    currentCompany: {
      document_number: '',
      document_type: '',
      name: '',
      id: '',
      is_legal_person: false,
      is_seller: false,
      is_buyer: false,
    },
    // TODO: Feature Flag is based on company id for now.
    features: {},
    userValidatedByOwner: false,
  },
  status: STORE_STATUS_INITIALIZED,
  error: null,
}

export const fetchUserByToken = createAsyncThunk<
  UserState,
  Promise<UserState>,
  Payload
>(
  STORE_USER_FETCH_BY_TOKEN,
  async (promise: Promise<UserState>, { rejectWithValue }) => {
    try {
      return await promise
    } catch (e: unknown) {
      const { code, message } = e as ErrorStore
      return rejectWithValue({ code, message })
    }
  }
)

const extraReducers: ExtraReducers = createReducers<
  UserState,
  UserState,
  Payload
>(fetchUserByToken, initialState.data)

const userSlice = createGenericSlice({
  name: STORE_USER,
  initialState,
  reducers: {
    addCompanies: (state, action: PayloadAction<Company[]>) => {
      const companies = mergeLegalPersonWithCompanies(
        action.payload,
        state.data.companies
      )
      const currentCompany =
        !state.data.currentCompany.id && companies[0]
          ? companies[0]
          : state.data.currentCompany
      return {
        ...state,
        data: {
          ...state.data,
          companies: mergeLegalPersonWithCompanies(
            action.payload,
            state.data.companies
          ),
          currentCompany,
        },
      }
    },
    setUserValidatedByOwner: (state, action: PayloadAction<boolean>) => {
      state.data.userValidatedByOwner = action.payload
    },
    setCurrentCompany: (state, action: PayloadAction<Company>) => {
      state.data.currentCompany = action.payload
    },
  },
  extraReducers,
})

function mergeLegalPersonWithCompanies(
  newCompanies: Company[],
  currentCompanies: Company[]
): Company[] {
  const companies: Company[] = [...currentCompanies]
  newCompanies.forEach((company) => {
    if (
      !currentCompanies.find(
        (c) => c.document_number === company.document_number
      )
    ) {
      companies.push(company)
    }
  })
  return companies
}

const { actions, reducer } = userSlice

const {
  clear,
  success,
  addCompanies,
  setUserValidatedByOwner,
  setCurrentCompany,
} = actions

export {
  clear,
  success,
  addCompanies,
  mergeLegalPersonWithCompanies,
  setUserValidatedByOwner,
  setCurrentCompany,
}

export default reducer
